import React, { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApp } from "@/context/AppContext";
import { api, shiftMonth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Check, Repeat, ScanLine, Loader2, Sparkles } from "lucide-react";

export const CategoryOptions = ({ cats }) => {
  const parents = cats.filter((c) => !c.parent_id);
  return (
    <>
      {parents.map((p) => (
        <React.Fragment key={p.id}>
          <SelectItem value={p.id} className="font-semibold">{p.name}</SelectItem>
          {cats.filter((c) => c.parent_id === p.id).map((s) => (
            <SelectItem key={s.id} value={s.id} className="pl-8 text-slate-300">↳ {s.name}</SelectItem>
          ))}
        </React.Fragment>
      ))}
    </>
  );
};

export const TransactionModal = ({ cats, trigger, transaction = null }) => {
  const qc = useQueryClient();
  const { activeUser } = useApp();
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const editing = !!transaction;
  const blank = { amount: "", merchant: "", date: today, category_id: "", note: "" };
  const [form, setForm] = useState(blank);
  const [repeat, setRepeat] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const receiptRef = useRef(null);

  React.useEffect(() => {
    if (open && editing) {
      setForm({
        amount: String(transaction.amount ?? ""),
        merchant: transaction.merchant || "",
        date: transaction.date || today,
        category_id: transaction.category_id || "",
        note: transaction.note || "",
      });
    } else if (open && !editing) {
      setForm(blank);
      setRepeat(false);
      setScanned(false);
    }
    // eslint-disable-next-line
  }, [open]);

  const onReceipt = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.scanReceipt(fd);
      setForm((f) => ({
        ...f,
        amount: res.amount != null ? String(res.amount) : f.amount,
        merchant: res.merchant || f.merchant,
        date: res.date || f.date,
        category_id: res.category_id || f.category_id,
      }));
      setScanned(true);
      const bits = [res.merchant, res.amount != null ? `$${res.amount}` : null].filter(Boolean).join(" · ");
      toast.success("Receipt scanned", { description: bits ? `Found ${bits}. Review & save.` : "Review the details & save." });
    } catch (err) {
      toast.error("Couldn't read that receipt", { description: err?.response?.data?.detail || "Try a clearer, well-lit photo." });
    } finally {
      setScanning(false);
    }
  };

  const mut = useMutation({
    mutationFn: (payload) => (editing ? api.updateTransaction(transaction.id, payload) : api.createTransaction(payload)),
    onSuccess: async (created) => {
      if (!editing && repeat && created) {
        const m = form.date.slice(0, 7);
        await api.createRecurring({
          amount: parseFloat(form.amount),
          merchant: form.merchant.trim(),
          category_id: form.category_id || null,
          user: activeUser,
          day: parseInt(form.date.slice(8, 10), 10) || 1,
          note: form.note || null,
          start_month: shiftMonth(m, 1),
        });
      }
      qc.invalidateQueries();
      toast.success(editing ? "Transaction updated" : repeat ? "Added & set to repeat monthly" : "Transaction added");
      setOpen(false);
      setForm(blank);
      setRepeat(false);
    },
  });

  const submit = () => {
    if (!form.amount || !form.merchant) return toast.error("Amount and merchant are required");
    mut.mutate({
      amount: parseFloat(form.amount),
      merchant: form.merchant.trim(),
      date: form.date,
      category_id: form.category_id || null,
      note: form.note || null,
      user: editing ? transaction.user : activeUser,
    });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#1a2232] border-[#2a364f] text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-heading">{editing ? "Edit transaction" : "Log transaction"}</DialogTitle>
          <DialogDescription className="text-slate-400">Enter the amount, merchant, date and category for this expense.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!editing && (
            <>
              <input ref={receiptRef} data-testid="receipt-file-input" type="file" accept="image/png,image/jpeg,image/webp" capture="environment" className="hidden" onChange={onReceipt} />
              <button
                type="button"
                data-testid="scan-receipt-button"
                onClick={() => receiptRef.current?.click()}
                disabled={scanning}
                className="w-full flex items-center gap-3 rounded-xl border border-dashed border-[#f97316]/40 bg-[#f97316]/5 px-4 py-3 hover:bg-[#f97316]/10 transition-colors disabled:opacity-60"
              >
                <div className="w-9 h-9 rounded-lg bg-[#f97316]/15 flex items-center justify-center text-[#f97316]">
                  {scanning ? <Loader2 className="w-4.5 h-4.5 animate-spin" style={{ width: 18, height: 18 }} /> : <ScanLine style={{ width: 18, height: 18 }} />}
                </div>
                <div className="text-left flex-1">
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    {scanning ? "Reading receipt…" : "Scan a receipt"}
                    {scanned && !scanning && <Sparkles className="w-3.5 h-3.5 text-[#22c55e]" />}
                  </div>
                  <div className="text-[11px] text-slate-500">{scanning ? "Extracting merchant, amount & date" : "Snap or upload a photo — we'll fill it in"}</div>
                </div>
              </button>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount</Label>
              <Input data-testid="transaction-amount-input" type="number" value={form.amount} onChange={set("amount")} placeholder="0.00" className="bg-[#0a0e14] border-[#2a364f] font-mono-num" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input data-testid="transaction-date-input" type="date" value={form.date} onChange={set("date")} className="bg-[#0a0e14] border-[#2a364f]" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Merchant</Label>
            <Input data-testid="transaction-merchant-input" value={form.merchant} onChange={set("merchant")} placeholder="e.g. Trader Joe's" className="bg-[#0a0e14] border-[#2a364f]" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
              <SelectTrigger data-testid="transaction-category-select" className="bg-[#0a0e14] border-[#2a364f]">
                <SelectValue placeholder="Assign a category" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a2232] border-[#2a364f] text-slate-100 max-h-72">
                <CategoryOptions cats={cats} />
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Textarea data-testid="transaction-note-input" value={form.note} onChange={set("note")} placeholder="Add a note…" className="bg-[#0a0e14] border-[#2a364f] resize-none" rows={2} />
          </div>
          {!editing && (
            <div className="w-full flex items-center gap-2.5 rounded-lg border border-[#2a364f] bg-[#0a0e14] px-3 py-2.5 hover:border-[#22c55e]/50 transition-colors">
              <Checkbox data-testid="repeat-monthly-checkbox" id="repeat-monthly" checked={repeat} onCheckedChange={(c) => setRepeat(!!c)} className="border-[#2a364f] data-[state=checked]:bg-[#22c55e] data-[state=checked]:border-[#22c55e]" />
              <Repeat className="w-4 h-4 text-[#22c55e]" />
              <label htmlFor="repeat-monthly" className="flex-1 cursor-pointer select-none">
                <div className="text-sm font-medium">Repeat monthly</div>
                <div className="text-[11px] text-slate-500">Auto-add this bill on day {form.date.slice(8, 10)} of each upcoming month</div>
              </label>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="save-transaction-button" onClick={submit} className="bg-[#f97316] hover:bg-[#ea580c] text-white gap-2">
            {editing ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editing ? "Save changes" : "Add transaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
